/**
 * `/admin/members/{id}/memberships` - spec 06's admin half, surfaced by spec 12's
 * Memberships section.
 *
 * Money IS shown here and only here: `amount_paid`, `payment_date`,
 * `payment_method` and `notes` are admin-only fields that ARCHITECTURE.md
 * requires member payloads to omit. This module is the admin half of that split.
 *
 * Weight is deliberately absent: body metrics are member-only (FR-5.8), so there
 * is no admin weight route to call.
 */

import { request } from "./client";
import type { PaymentMethod } from "./types";

export type MembershipExtension = {
  id: string;
  previous_expiry: string;
  new_expiry: string;
  reason: string;
  actor_admin_id: string | null;
  created_at: string;
};

export type Membership = {
  id: string;
  user_id: string;
  plan_name: string;
  plan_features: string[];
  start_date: string;
  expiry_date: string;
  original_expiry_date: string;
  amount_paid: string | null;
  currency: string;
  payment_date: string | null;
  payment_method: PaymentMethod | null;
  notes: string | null;
  /** 0 or 1 - FR-3.5.7 allows one extension, then the record is locked. */
  extension_count: number;
  /** True once extended; the extend control is disabled, not hidden, so the
   *  reason the record is locked stays visible. */
  is_locked: boolean;
  extension: MembershipExtension | null;
  days_remaining: number;
  created_by_admin_id: string | null;
  created_at: string;
  updated_at: string;
};

export type MemberAccess = {
  has_app_access: boolean;
  in_grace_period: boolean;
  days_remaining: number;
  access_until: string;
  current_membership_id: string | null;
};

export type MembershipList = {
  items: Membership[];
  access: MemberAccess;
};

export type MembershipOutcome = {
  membership: Membership;
  message: string;
};

export type MembershipCreate = {
  plan_name: string;
  plan_features?: string[];
  start_date: string;
  expiry_date: string;
  amount_paid?: string;
  payment_date?: string;
  payment_method?: PaymentMethod;
  notes?: string;
};

export type MembershipUpdate = {
  plan_name?: string;
  plan_features?: string[];
  amount_paid?: string;
  payment_date?: string;
  payment_method?: PaymentMethod;
  notes?: string;
};

export function fetchMemberships(userId: string): Promise<MembershipList> {
  return request<MembershipList>(`/admin/members/${userId}/memberships`);
}

export function fetchMembership(userId: string, membershipId: string): Promise<MembershipOutcome> {
  return request<MembershipOutcome>(`/admin/members/${userId}/memberships/${membershipId}`);
}

export function createMembership(userId: string, payload: MembershipCreate): Promise<MembershipOutcome> {
  return request<MembershipOutcome>(`/admin/members/${userId}/memberships`, {
    method: "POST",
    body: payload,
  });
}

export function updateMembership(
  userId: string,
  membershipId: string,
  payload: MembershipUpdate,
): Promise<MembershipOutcome> {
  return request<MembershipOutcome>(`/admin/members/${userId}/memberships/${membershipId}`, {
    method: "PATCH",
    body: payload,
  });
}

/**
 * Extend an expiry (FR-3.5.2). One per record: a second attempt answers 409
 * `membership_already_extended`, and a date that is not strictly later answers
 * 422 `extension_not_forward` with the current expiry in its sentence.
 */
export function extendMembership(
  userId: string,
  membershipId: string,
  newExpiry: string,
  reason: string,
): Promise<MembershipOutcome> {
  return request<MembershipOutcome>(`/admin/members/${userId}/memberships/${membershipId}/extend`, {
    method: "POST",
    body: { new_expiry: newExpiry, reason },
  });
}
